import React from "react";
import _ from "lodash";
import {parseResponse} from "../../../utils/api";
import {generateQueryParamsHash, getPtfTrackingStartDate} from "../utils";
import {useTabsSharedDataContext} from "../components_v2/TabsSharedData/TabsSharedData";
import {isCancel, useCancelableRequest} from "../../../hooks/useCancelableRequest";

const SHARED_DATA_KEY ='correlation-matrix-data';

/**
 * Fetch portfolio risk return and correlation matrix data.
 * @param customerId Customer identifier
 * @param portfolio Portfolio data
 * @param dataProvider Data provider
 * Set to true in case hook wrapped in context provider that already fetch data and it could be reused.
 * @param startDate
 * @param endDate
 */
export function useCorrelationMatrixData(customerId, portfolio, dataProvider, startDate, endDate) {

  const [data, setData] = React.useState({
    correlationData: undefined,
    correlationLoading: true,
    correlationError: null,
    updatedAt: +new Date(),
  });

  const paramsForHash = [startDate, endDate];
  const sharedData = useTabsSharedDataContext();
  const getCancelToken = useCancelableRequest();
  const dataKey = `${SHARED_DATA_KEY}-${portfolio.depotNumber}`;

  const fetch = () => {
    const cancelToken = getCancelToken();
    const hashKey = generateQueryParamsHash(customerId, [portfolio], ...paramsForHash);

    const _setData = (state, onlyCache=false) => {
      // It is not possible to sync data with shared data in
      // hook because hook is not executed if component destroyed
      if(!onlyCache) setData(state);
      sharedData.setDataValue(dataKey, hashKey, state);
    };

    _setData({
      correlationData: undefined,
      correlationLoading: true,
      correlationError: null,
      updatedAt: +new Date(),
    });
    dataProvider.getCorrelation(customerId, portfolio.depotNumberForAssets || portfolio.depotNumber, startDate || getPtfTrackingStartDate(portfolio), endDate, undefined, cancelToken)
      .then((response) => {
        parseResponse(response, 'correlation_matrix',
          (data) => {
            _setData({
              correlationError: undefined,
              correlationData: data,
              correlationLoading: false,
              updatedAt: +new Date()
            })
          },
          (error) => {
            _setData({
              correlationData: undefined,
              correlationLoading: false,
              correlationError: error,
              updatedAt: +new Date()
            })
          })
      })
      .catch((error) => {
        _setData({
          correlationData: undefined,
          correlationLoading: false,
          correlationError: error.toString(),
          updatedAt: +new Date()
        }, isCancel(error))
      })
  };

  const syncFromShared = () => {
    const sharedRiskReturnData = !!sharedData
      ? sharedData.getDataValue(dataKey, generateQueryParamsHash(customerId, [portfolio], ...paramsForHash))
      : undefined;

    if (!!sharedRiskReturnData && !sharedRiskReturnData.correlationError && sharedRiskReturnData.updatedAt !== data.updatedAt) {
      // sync internal hook data with shared data.
      setData(sharedRiskReturnData);

      return true
    } else {
      return false;
    }
  };

  React.useEffect(() => {syncFromShared()}, [_.get(sharedData, 'updatedAt')]);

  React.useEffect(() => {
    if (!!customerId && !!portfolio) {
      const synced = syncFromShared();

      if (!synced) {
        fetch();
      }
    }
  }, [customerId, _.get(portfolio, 'depotNumberForAssets'), _.get(portfolio, 'depotNumber'), startDate, endDate]);

  return data

}